import { useEffect, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { isTelegram, tg } from '@/lib/telegram';
import { useAuthStore } from '@/store/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from './ui/input';

export const AuthGate = ({ children }: { children: React.ReactNode }) => {
  const { token, master, setAuth } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [devTgId, setDevTgId] = useState('');
  const [devName, setDevName] = useState('');

  const useMockAuth = import.meta.env.VITE_DEV_MOCK_AUTH === 'true';

  useEffect(() => {
    let cancelled = false;
    // Read latest master via store getter to avoid retriggering on each setAuth.
    const run = async () => {
      try {
        const currentToken = useAuthStore.getState().token;
        const currentMaster = useAuthStore.getState().master;
        if (currentToken && currentMaster) {
          try {
            const { data } = await api.get('/me');
            if (data?.master && !cancelled) {
              setAuth(currentToken, { ...currentMaster, ...data.master });
            }
            if (!cancelled) setLoading(false);
            return;
          } catch (e: any) {
            if (e?.response?.status !== 401) throw e;
            useAuthStore.getState().clear();
          }
        }
        if (isTelegram()) {
          const initData = tg()!.initData;
          const { data: login } = await api.post('/auth/telegram', { initData });
          if (cancelled) return;
          setAuth(login.token, login.master);
          try {
            const { data: me } = await api.get('/me');
            if (me?.master && !cancelled) setAuth(login.token, { ...login.master, ...me.master });
          } catch {}
          if (!cancelled) setLoading(false);
          return;
        }
        if (!cancelled) setLoading(false);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.error ?? e?.message ?? 'Ошибка авторизации');
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const devLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post('/auth/dev', { telegram_id: devTgId, name: devName });
      setAuth(data.token, data.master);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Ошибка');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="relative flex h-screen items-center justify-center bg-background">
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-aurora opacity-90" />
        <div className="relative flex flex-col items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand text-white shadow-soft-lg">
            <Sparkles className="h-7 w-7" />
          </div>
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (token && master) return <>{children}</>;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background p-6">
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-aurora opacity-90" />
      <Card className="relative w-full max-w-md">
        <CardContent className="space-y-5 p-6 pt-7">
          <div className="flex flex-col items-center text-center">
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand text-white shadow-soft-lg">
              <Sparkles className="h-8 w-8" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="text-gradient">Chernova</span> CRM
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {error
                ? error
                : 'Профессиональная CRM для мастера наращивания волос'}
            </p>
          </div>

          {useMockAuth && (
            <div className="space-y-2.5 border-t border-border/60 pt-4">
              <p className="text-center text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Dev-вход
              </p>
              <Input
                placeholder="Telegram ID"
                value={devTgId}
                onChange={(e) => setDevTgId(e.target.value)}
              />
              <Input
                placeholder="Имя"
                value={devName}
                onChange={(e) => setDevName(e.target.value)}
              />
              <Button
                onClick={devLogin}
                size="lg"
                className="w-full"
                disabled={!devTgId || !devName}
              >
                Войти
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
